/**
 * Live Quiz Analytics Component
 *
 * Comprehensive analytics dashboard for live quiz sessions including:
 * - Session overview metrics
 * - Question-by-question performance
 * - Player engagement analysis
 * - Response time analytics
 * - Streak and scoring insights
 * - Answer distribution charts
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../lib/api';
import { Skeleton } from '../../ui/Skeleton';

interface QuestionPerformance {
  questionNumber: number;
  questionText: string;
  totalResponses: number;
  correctResponses: number;
  incorrectResponses: number;
  noResponse: number;
  accuracyRate: number;
  avgResponseTime: number;
  fastestResponse: number;
  slowestResponse: number;
  answerDistribution: Array<{
    optionText: string;
    count: number;
    percentage: number;
    isCorrect: boolean;
  }>;
}

interface PlayerPerformance {
  playerId: string;
  playerName: string;
  totalScore: number;
  correctAnswers: number;
  incorrectAnswers: number;
  accuracy: number;
  avgResponseTime: number;
  bestStreak: number;
  finalRank: number;
  engagementScore: number;
}

interface LiveQuizAnalyticsData {
  sessionId: string;
  sessionDate: string;
  totalQuestions: number;
  totalPlayers: number;
  completedPlayers: number;
  dropoutRate: number;
  avgSessionDuration: number;
  avgScore: number;
  highestScore: number;
  lowestScore: number;
  overallAccuracy: number;
  avgResponseTime: number;
  questionPerformance: QuestionPerformance[];
  playerPerformance: PlayerPerformance[];
  engagementMetrics: {
    peakPlayers: number;
    avgPlayersPerQuestion: number;
    questionWithMostDropoffs: number;
    questionWithBestEngagement: number;
  };
  scoreDistribution: Array<{
    range: string;
    count: number;
    percentage: number;
  }>;
  timeAnalytics: {
    fastestPlayer: string;
    slowestPlayer: string;
    avgTimeDifference: number;
  };
}

interface LiveQuizAnalyticsProps {
  sessionId: string;
  formId?: string;
  isOpen: boolean;
  onClose: () => void;
}

export default function LiveQuizAnalytics({ sessionId, formId, isOpen, onClose }: LiveQuizAnalyticsProps) {
  const { t } = useTranslation('liveQuiz');
  const [data, setData] = useState<LiveQuizAnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<'overview' | 'questions' | 'players' | 'engagement'>('overview');

  useEffect(() => {
    if (isOpen && (sessionId || formId)) {
      loadAnalytics();
    }
  }, [isOpen, sessionId, formId]);

  async function loadAnalytics() {
    setLoading(true);
    setError(null);
    try {
      // If sessionId is provided, get analytics for that specific session
      // If only formId is provided, get analytics for the most recent session or aggregated data
      const endpoint = sessionId
        ? `/api/live-quiz/sessions/${sessionId}/analytics`
        : `/api/forms/${formId}/live-quiz-analytics`;

      const response = await api.get(endpoint);
      if (!response.ok) throw new Error('Failed to load analytics');
      const result = await response.json() as LiveQuizAnalyticsData;
      setData(result);
    } catch (err) {
      console.error('Failed to load analytics:', err);
      setError(t('analytics.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }

  function formatTime(seconds: number): string {
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  }

  function exportToCSV() {
    if (!data) return;

    // Create CSV content
    let csv = 'Player Name,Score,Correct Answers,Accuracy,Avg Response Time,Best Streak,Final Rank\n';
    data.playerPerformance.forEach(player => {
      csv += `${player.playerName},${player.totalScore},${player.correctAnswers},${player.accuracy}%,${player.avgResponseTime}s,${player.bestStreak},${player.finalRank}\n`;
    });

    // Download
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `quiz-${sessionId}-analytics.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('analytics.title')}</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('analytics.sessionId')}: {sessionId} • {data?.sessionDate}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {data && (
              <button
                onClick={exportToCSV}
                className="px-3 py-1.5 text-sm bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                {t('analytics.exportCsv')}
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700 px-6">
          <div className="flex gap-6">
            {[
              { id: 'overview', label: t('analytics.tabs.overview') },
              { id: 'questions', label: t('analytics.tabs.questions') },
              { id: 'players', label: t('analytics.tabs.players') },
              { id: 'engagement', label: t('analytics.tabs.engagement') },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveView(tab.id as any)}
                className={`px-1 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeView === tab.id
                    ? 'border-orange-500 text-orange-600 dark:text-orange-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="space-y-6">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-32 w-full rounded-lg" />
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-600 dark:text-red-400">{error}</p>
              <button
                onClick={loadAnalytics}
                className="mt-4 text-orange-600 dark:text-orange-400 hover:underline"
              >
                {t('analytics.errors.tryAgain')}
              </button>
            </div>
          ) : data ? (
            <>
              {/* Overview Tab */}
              {activeView === 'overview' && (
                <div className="space-y-6">
                  {/* Key Metrics */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <MetricCard
                      label={t('analytics.metrics.totalPlayers')}
                      value={data.totalPlayers}
                      icon="👥"
                      color="blue"
                    />
                    <MetricCard
                      label={t('analytics.metrics.completionRate')}
                      value={`${Math.round((data.completedPlayers / data.totalPlayers) * 100)}%`}
                      icon="✅"
                      color="green"
                    />
                    <MetricCard
                      label={t('analytics.metrics.avgScore')}
                      value={Math.round(data.avgScore)}
                      icon="🎯"
                      color="purple"
                    />
                    <MetricCard
                      label={t('analytics.metrics.avgAccuracy')}
                      value={`${data.overallAccuracy}%`}
                      icon="📊"
                      color="orange"
                    />
                  </div>

                  {/* Score Distribution */}
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-6">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
                      {t('analytics.sections.scoreDistribution')}
                    </h3>
                    <div className="space-y-3">
                      {data.scoreDistribution.map((dist, idx) => (
                        <div key={idx} className="flex items-center gap-3">
                          <span className="text-sm text-gray-600 dark:text-gray-400 w-24">
                            {dist.range}
                          </span>
                          <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-8 relative overflow-hidden">
                            <div
                              className="bg-gradient-to-r from-orange-500 to-orange-600 h-full rounded-full flex items-center px-3 transition-all"
                              style={{ width: `${dist.percentage}%` }}
                            >
                              <span className="text-xs font-medium text-white">
                                {dist.count} {t('analytics.labels.players')} ({dist.percentage}%)
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Top Performers */}
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-6">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
                      🏆 {t('analytics.sections.topPerformers')}
                    </h3>
                    <div className="space-y-2">
                      {data.playerPerformance.slice(0, 5).map((player, idx) => (
                        <div
                          key={player.playerId}
                          className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <span className={`w-8 h-8 flex items-center justify-center rounded-full font-bold ${
                              idx === 0 ? 'bg-yellow-500 text-yellow-900' :
                              idx === 1 ? 'bg-gray-300 text-gray-700' :
                              idx === 2 ? 'bg-orange-400 text-orange-900' :
                              'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                            }`}>
                              {idx + 1}
                            </span>
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">{player.playerName}</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {player.correctAnswers}/{data.totalQuestions} {t('analytics.labels.correct')} • {player.accuracy}% {t('analytics.labels.accuracy')}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-orange-600 dark:text-orange-400">
                              {player.totalScore}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{t('analytics.labels.points')}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Questions Tab */}
              {activeView === 'questions' && (
                <div className="space-y-4">
                  {data.questionPerformance.map((question) => (
                    <div
                      key={question.questionNumber}
                      className="bg-gray-50 dark:bg-gray-900 rounded-lg p-6"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="px-2 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 text-xs font-semibold rounded">
                              Q{question.questionNumber}
                            </span>
                            <h4 className="font-medium text-gray-900 dark:text-white">
                              {question.questionText}
                            </h4>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                            <span>{question.totalResponses} {t('analytics.labels.responses')}</span>
                            <span>•</span>
                            <span className="text-green-600 dark:text-green-400">
                              {question.accuracyRate}% {t('analytics.labels.accuracy')}
                            </span>
                            <span>•</span>
                            <span>{t('analytics.labels.avg')}: {question.avgResponseTime.toFixed(1)}s</span>
                          </div>
                        </div>
                      </div>

                      {/* Answer Distribution */}
                      <div className="space-y-2">
                        {question.answerDistribution.map((option, idx) => (
                          <div key={idx} className="flex items-center gap-3">
                            <span className={`w-8 h-8 flex items-center justify-center rounded font-bold text-white ${
                              ['bg-red-500', 'bg-blue-500', 'bg-yellow-500', 'bg-green-500', 'bg-purple-500'][idx % 5]
                            }`}>
                              {String.fromCharCode(65 + idx)}
                            </span>
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                  {option.optionText}
                                  {option.isCorrect && (
                                    <span className="ml-2 text-green-600 dark:text-green-400">✓</span>
                                  )}
                                </span>
                                <span className="text-sm font-medium text-gray-900 dark:text-white">
                                  {option.count} ({option.percentage}%)
                                </span>
                              </div>
                              <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                <div
                                  className={`h-full ${option.isCorrect ? 'bg-green-500' : 'bg-gray-400 dark:bg-gray-600'}`}
                                  style={{ width: `${option.percentage}%` }}
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Players Tab */}
              {activeView === 'players' && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" dir="auto">
                    <thead>
                      <tr className="text-gray-500 dark:text-gray-400 border-b dark:border-gray-700">
                        <th className="pb-3 px-2 font-medium text-start">{t('analytics.labels.rank')}</th>
                        <th className="pb-3 px-2 font-medium text-start">{t('analytics.labels.player')}</th>
                        <th className="pb-3 px-2 font-medium text-start">{t('analytics.labels.score')}</th>
                        <th className="pb-3 px-2 font-medium text-start">{t('analytics.labels.correct')}</th>
                        <th className="pb-3 px-2 font-medium text-start">{t('analytics.labels.accuracy')}</th>
                        <th className="pb-3 px-2 font-medium text-start">{t('analytics.labels.avgTime')}</th>
                        <th className="pb-3 px-2 font-medium text-start">{t('analytics.labels.bestStreak')}</th>
                        <th className="pb-3 px-2 font-medium text-start">{t('analytics.labels.engagement')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.playerPerformance.map((player) => (
                        <tr
                          key={player.playerId}
                          className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-900"
                        >
                          <td className="py-3 px-2 text-start">
                            <span className="font-medium text-gray-900 dark:text-white">
                              #{player.finalRank}
                            </span>
                          </td>
                          <td className="py-3 px-2 text-start font-medium text-gray-900 dark:text-white">
                            {player.playerName}
                          </td>
                          <td className="py-3 px-2 text-start font-semibold text-orange-600 dark:text-orange-400">
                            {player.totalScore}
                          </td>
                          <td className="py-3 px-2 text-start text-gray-700 dark:text-gray-300">
                            {player.correctAnswers}/{data.totalQuestions}
                          </td>
                          <td className="py-3 px-2 text-start">
                            <span className={`${
                              player.accuracy >= 70 ? 'text-green-600 dark:text-green-400' :
                              player.accuracy >= 50 ? 'text-yellow-600 dark:text-yellow-400' :
                              'text-red-600 dark:text-red-400'
                            }`}>
                              {player.accuracy}%
                            </span>
                          </td>
                          <td className="py-3 px-2 text-start text-gray-700 dark:text-gray-300">
                            {player.avgResponseTime.toFixed(1)}s
                          </td>
                          <td className="py-3 px-2 text-start text-gray-700 dark:text-gray-300">
                            {player.bestStreak} 🔥
                          </td>
                          <td className="py-3 px-2 text-start">
                            <div className="w-16 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-blue-500"
                                style={{ width: `${player.engagementScore}%` }}
                              />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Engagement Tab */}
              {activeView === 'engagement' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <MetricCard
                      label={t('analytics.metrics.peakPlayers')}
                      value={data.engagementMetrics.peakPlayers}
                      icon="📈"
                      color="blue"
                    />
                    <MetricCard
                      label={t('analytics.metrics.avgPlayersPerQuestion')}
                      value={Math.round(data.engagementMetrics.avgPlayersPerQuestion)}
                      icon="👥"
                      color="green"
                    />
                    <MetricCard
                      label={t('analytics.metrics.dropoutRate')}
                      value={`${Math.round(data.dropoutRate)}%`}
                      icon="📉"
                      color="red"
                    />
                    <MetricCard
                      label={t('analytics.metrics.avgSession')}
                      value={formatTime(data.avgSessionDuration)}
                      icon="⏱️"
                      color="purple"
                    />
                  </div>

                  {/* Response Time Analysis */}
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-6">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
                      ⚡ {t('analytics.sections.responseTimeAnalysis')}
                    </h3>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('analytics.labels.fastestPlayer')}</p>
                        <p className="text-lg font-semibold text-green-600 dark:text-green-400">
                          {data.timeAnalytics.fastestPlayer}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('analytics.labels.slowestPlayer')}</p>
                        <p className="text-lg font-semibold text-orange-600 dark:text-orange-400">
                          {data.timeAnalytics.slowestPlayer}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('analytics.labels.avgDifference')}</p>
                        <p className="text-lg font-semibold text-gray-900 dark:text-white">
                          {data.timeAnalytics.avgTimeDifference.toFixed(1)}s
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Question Engagement Heatmap */}
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-6">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
                      {t('analytics.sections.questionEngagementHeatmap')}
                    </h3>
                    <div className="space-y-2">
                      {data.questionPerformance.map((q) => {
                        const engagementRate = (q.totalResponses / data.totalPlayers) * 100;
                        return (
                          <div key={q.questionNumber} className="flex items-center gap-3">
                            <span className="text-sm text-gray-600 dark:text-gray-400 w-12">
                              Q{q.questionNumber}
                            </span>
                            <div className="flex-1 h-8 bg-gray-200 dark:bg-gray-700 rounded overflow-hidden">
                              <div
                                className={`h-full flex items-center px-3 ${
                                  engagementRate >= 90 ? 'bg-green-500' :
                                  engagementRate >= 70 ? 'bg-yellow-500' :
                                  engagementRate >= 50 ? 'bg-orange-500' :
                                  'bg-red-500'
                                }`}
                                style={{ width: `${engagementRate}%` }}
                              >
                                <span className="text-xs font-medium text-white">
                                  {q.totalResponses}/{data.totalPlayers} ({Math.round(engagementRate)}%)
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// Metric Card Component
interface MetricCardProps {
  label: string;
  value: string | number;
  icon: string;
  color?: 'blue' | 'green' | 'orange' | 'purple' | 'red';
}

function MetricCard({ label, value, icon, color = 'blue' }: MetricCardProps) {
  const colorClasses = {
    blue: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800',
    green: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800',
    orange: 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800',
    purple: 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800',
    red: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
  };

  return (
    <div className={`${colorClasses[color]} border rounded-lg p-4`}>
      <div className="flex items-center gap-3">
        <span className="text-3xl">{icon}</span>
        <div>
          <p className="text-xs text-gray-600 dark:text-gray-400">{label}</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
        </div>
      </div>
    </div>
  );
}
